import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import Select from "@/components/Select/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useGetUserPrograms } from "@/hooks/usePrograms";
import { useOrganizationUnitsByIds } from "@/hooks/useOrganizations";
import { SkeletonClientFilters } from "@/ui/menus/clients/Skeletons";
import { ParticipantStatus } from "@/domain/participant.enum";

export type Filters = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string[];
  programId: string;
  orgUnitId: string;
};

type Props = {
  filtersState: [Filters, React.Dispatch<React.SetStateAction<Filters>>];
};

const ClientFilters: FC<Props> = ({ filtersState }) => {
  const theme = useAppTheme();
  const [filters, setFilters] = filtersState;
  const {
    userInfo,
    hasSystemAdminRole,
    hasStandardAccountRole,
    hasCustomRole,
  } = useUserStore();

  const isRestrictedUser = hasCustomRole || hasStandardAccountRole;

  const { programs, isLoadingPrograms, isFetchingPrograms } =
    useGetUserPrograms({
      userId: userInfo?._id as string,
      sortType: "desc",
      hasSystemAdminRole,
    });

  const programsOptions = useMemo(() => {
    if (!programs) return [];
    return programs.map((program) => ({
      key: program._id,
      value: program.name,
    }));
  }, [programs]);

  // Organization Units filtering
  const [selectedOrgUnitIds, setSelectedOrgUnitIds] = useState<string[]>([]);

  const {
    organizationUnits,
    isLoadingOrganizationUnits,
    refetchOrganizationUnits,
  } = useOrganizationUnitsByIds({
    ids: selectedOrgUnitIds,
    status: "ACTIVE",
  });

  const orgUnitsOptions = useMemo(() => {
    if (!organizationUnits) return [];

    if (hasSystemAdminRole) {
      return organizationUnits.map((orgUnit) => ({
        key: orgUnit._id,
        value: orgUnit.name,
      }));
    }

    if (isRestrictedUser && userInfo?.programs) {
      const userAccessibleOrgUnitIds = userInfo.programs.flatMap(
        (program) => program.orgUnitIds
      );

      const filteredOrgUnits = organizationUnits.filter((orgUnit) =>
        userAccessibleOrgUnitIds.includes(orgUnit._id)
      );

      return filteredOrgUnits.map((orgUnit) => ({
        key: orgUnit._id,
        value: orgUnit.name,
      }));
    }

    return organizationUnits.map((orgUnit) => ({
      key: orgUnit._id,
      value: orgUnit.name,
    }));
  }, [
    organizationUnits,
    hasSystemAdminRole,
    isRestrictedUser,
    userInfo?.programs,
  ]);

  // Update organization units when program changes
  useEffect(() => {
    if (programs && programs.length > 0) {
      const selectedProgram = programs.find(
        (program) => program._id === filters.programId
      );

      if (selectedProgram) {
        let orgUnitIds =
          selectedProgram.orgUnitIds || selectedProgram.orgUnitId || [];

        // AC01: For CUSTOM_ROLE and STANDARD_ACCOUNT_ROLE, filter by user access
        if (isRestrictedUser && userInfo?.programs) {
          const userProgramAccess = userInfo.programs.find(
            (userProgram) => userProgram.programId === filters.programId
          );

          if (userProgramAccess) {
            orgUnitIds = orgUnitIds.filter((orgUnitId) =>
              userProgramAccess.orgUnitIds.includes(orgUnitId)
            );
          } else {
            orgUnitIds = [];
          }
        }

        setSelectedOrgUnitIds(orgUnitIds);
      } else {
        setSelectedOrgUnitIds([]);
      }
    }
  }, [programs, filters.programId, isRestrictedUser, userInfo?.programs]);

  useEffect(() => {
    if (selectedOrgUnitIds.length > 0) {
      refetchOrganizationUnits();
    }
  }, [selectedOrgUnitIds]);

  useEffect(() => {
    if (programs && programs.length > 0 && !filters.programId) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        programId: programs[0]._id,
      }));
    }
  }, [programs, filters.programId, setFilters]);

  return (
    <Box
      sx={{
        display: "flex",
        gap: "1.25rem",
        width: "100%",
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "column",
        },
      }}
    >
      {isFetchingPrograms || isLoadingPrograms ? (
        <SkeletonClientFilters />
      ) : (
        <>
          <Box
            sx={{
              backgroundColor: theme.palette.surface.light2,
              px: 2,
              width: {
                xs: "100%",
                sm: "100%",
              },
            }}
            className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
          >
            <InputBase
              placeholder="Search participant"
              inputProps={{ "aria-label": "search" }}
              onChange={(e) => {
                setFilters((filters) => ({
                  ...filters,
                  search: e.target.value,
                  pageNumber: 0,
                }));
              }}
              value={filters.search}
            />
            <SearchIcon className="text-icon-primary" />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: "0.5rem",
              width: {
                xs: "100%",
                sm: "100%",
              },
              flexDirection: {
                xs: "column",
                sm: "row",
                md: "row",
              },
            }}
          >
            <Select
              handleChange={(value) => {
                setFilters((filters) => ({
                  ...filters,
                  programId: value as string,
                  orgUnitId: "", // Clear org unit when program changes
                  pageNumber: 0,
                }));
              }}
              data={programsOptions || []}
              value={filters.programId}
              label="Program"
              id="users-program"
              isLoading={isLoadingPrograms}
              maxWidth={320}
              fullWidth
            />

            <Select
              handleChange={(value) => {
                setFilters((filters) => ({
                  ...filters,
                  orgUnitId: value as string,
                  pageNumber: 0,
                }));
              }}
              data={orgUnitsOptions || []}
              value={filters.orgUnitId}
              label="Organization Unit"
              id="users-orgunit"
              isLoading={!filters.programId || isLoadingOrganizationUnits}
              maxWidth={320}
              fullWidth
              showClearOption
            />

            <Select
              handleChange={(value) => {
                setFilters((filters) => ({
                  ...filters,
                  status: value as string[],
                  pageNumber: 0,
                }));
              }}
              data={[
                { key: ParticipantStatus.ACTIVE, value: "Active" },
                { key: ParticipantStatus.COMPLETED, value: "Completed" },
                { key: ParticipantStatus.DISENROLLED, value: "Disenrolled" },
              ]}
              value={filters.status[0] === "" ? [] : filters.status}
              label="Status"
              id="participant-status"
              multiple
              maxWidth={320}
              fullWidth
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ClientFilters;
